<template>
  <div class="svc-dist">
    <div class="svc-dist__header">
      <div>
        <span class="svc-dist__title">Distribución por inquilino</span>
        <span class="svc-dist__realtime">Se actualiza en tiempo real</span>
      </div>
      <div class="svc-dist__view-toggle">
        <button class="svc-dist__view-pill" :class="{ 'svc-dist__view-pill--active': viewMode === 'tenant' }" @click="viewMode = 'tenant'">Por inquilino</button>
        <button class="svc-dist__view-pill" :class="{ 'svc-dist__view-pill--active': viewMode === 'service' }" @click="viewMode = 'service'">Por servicio</button>
      </div>
    </div>

    <div v-if="data.vacantInfo.count > 0" class="svc-dist__vacant-note">
      {{ data.vacantInfo.count }} nave{{ data.vacantInfo.count === 1 ? '' : 's' }} vacante{{ data.vacantInfo.count === 1 ? '' : 's' }} excluida{{ data.vacantInfo.count === 1 ? '' : 's' }} de la distribución · {{ data.vacantInfo.areaM2 }} m²
    </div>

    <Transition name="svc-dist-fade" mode="out-in">
      <!-- Vista por inquilino -->
      <div v-if="viewMode === 'tenant'" key="tenant" class="svc-dist__body">
        <div
          v-for="row in data.rows"
          :key="row.contractId"
          class="svc-dist__row"
          :class="{ 'svc-dist__row--exempt': row.exempt, 'svc-dist__row--highlight': highlightedRows.has(row.contractId) }"
          @mouseenter="onRowHover(row)"
          @mouseleave="$emit('hover-services', [])"
          @click="toggleExpand(row.contractId)"
        >
          <div class="svc-dist__row-main">
            <span class="svc-dist__status-dot" :class="`svc-dist__status-dot--${row.statusColor}`"></span>

            <div class="svc-dist__tenant-info">
              <div class="svc-dist__tenant-name">
                {{ row.tenantName }}
                <span v-if="row.exempt" class="svc-dist__exempt-tag">Exento</span>
              </div>
              <span class="svc-dist__plot-tag">{{ row.plotName }}</span>
            </div>

            <div class="svc-dist__area-info">
              <div>{{ row.areaM2 || 0 }} m²</div>
              <div class="svc-dist__area-percent">({{ row.percentOfTotal }}%)</div>
            </div>

            <div class="svc-dist__amount-wrap">
              <div class="svc-dist__amount" :class="{ 'svc-dist__amount--strike': row.inGrace, 'svc-dist__amount--gray': row.exempt }">
                {{ formatCurrency(row.inGrace ? row.graceOriginalTotal : row.total) }}
              </div>
              <div v-if="row.inGrace" class="svc-dist__grace-note">En periodo de gracia · $0 este mes</div>
              <div v-else-if="row.exemptTotal > 0" class="svc-dist__exempt-note">Exento {{ formatCurrency(row.exemptTotal) }}</div>
            </div>

            <span class="mdi mdi-chevron-down svc-dist__chevron" :class="{ 'svc-dist__chevron--open': expandedIds.has(row.contractId) }"></span>
          </div>

          <div v-if="expandedIds.has(row.contractId)" class="svc-dist__expanded" @click.stop>
            <div v-for="b in row.breakdown" :key="b.serviceId" class="svc-dist__breakdown-row">
              <span class="mdi svc-dist__breakdown-icon" :class="b.icon"></span>
              <span class="svc-dist__breakdown-name">
                {{ b.serviceName }}
                <span v-if="b.exemptAmount > 0" class="svc-dist__ex-tag" :class="`svc-dist__ex-tag--${b.exemptResolution}`">
                  Exento {{ b.exemptPercent }}% · {{ b.exemptResolution === 'ABSORB' ? 'absorbido' : 'anulado' }}
                </span>
              </span>
              <span v-if="b.exemptAmount > 0" class="svc-dist__breakdown-gross">{{ formatCurrency(b.grossAmount) }}</span>
              <span class="svc-dist__breakdown-amount">{{ formatCurrency(b.amount) }}</span>
              <button
                v-if="b.exemptAmount > 0"
                class="svc-dist__ex-remove"
                title="Quitar exención"
                :disabled="busyId === exemptionIdFor(row, b.serviceId)"
                @click="removeExemption(row, b.serviceId)"
              ><span class="mdi mdi-close"></span></button>
            </div>
            <div class="svc-dist__breakdown-total">
              <span>Total</span>
              <span>{{ formatCurrency(row.total) }}</span>
            </div>
            <div v-if="canExempt(row)" class="svc-dist__ex-actions">
              <button class="svc-dist__ex-btn" @click="exemptRow = row"><span class="mdi mdi-shield-off-outline"></span> Exentar servicios</button>
            </div>
            <p v-if="exError && exErrorId === row.contractId" class="svc-dist__ex-error">{{ exError }}</p>
          </div>
        </div>
      </div>

      <!-- Vista por servicio -->
      <div v-else key="service" class="svc-dist__body">
        <div v-for="s in data.byService" :key="s.serviceId" class="svc-dist__service-row">
          <div class="svc-dist__service-row-main" @click="toggleServiceExpand(s.serviceId)">
            <span class="mdi svc-dist__breakdown-icon" :class="s.icon"></span>
            <span class="svc-dist__service-name">{{ s.serviceName }}</span>
            <span class="svc-dist__service-amount">{{ formatCurrency(s.totalAmount) }}</span>
            <span class="mdi mdi-chevron-down svc-dist__chevron" :class="{ 'svc-dist__chevron--open': expandedServiceIds.has(s.serviceId) }"></span>
          </div>

          <div v-if="expandedServiceIds.has(s.serviceId)" class="svc-dist__expanded">
            <div v-for="cb in s.contractBreakdown" :key="cb.contractId" class="svc-dist__breakdown-row">
              <span class="svc-dist__breakdown-name">{{ cb.tenantName }} · {{ cb.plotName }}</span>
              <span class="svc-dist__breakdown-amount">{{ formatCurrency(cb.amount) }}</span>
            </div>
            <div class="svc-dist__breakdown-total">
              <template v-if="s.matches">
                <span class="mdi mdi-check-circle svc-dist__check-icon"></span>
                <span>Cuadra</span>
              </template>
              <template v-else>
                <span class="svc-dist__diff-text">Diferencia: {{ formatCurrency(s.difference) }}</span>
              </template>
            </div>
          </div>
        </div>
      </div>
    </Transition>

    <div class="svc-dist__footer">
      <span class="svc-dist__footer-label">Total distribuido</span>
      <span v-if="data.matches" class="svc-dist__footer-check"><span class="mdi mdi-check-circle"></span> Cuadra</span>
      <span v-else class="svc-dist__footer-diff">Diferencia: {{ formatCurrency(data.differenceAmount) }} · Ajuste automático aplicado</span>
      <span v-if="data.exemptTotal > 0" class="svc-dist__footer-exempt">
        Exentado {{ formatCurrency(data.exemptTotal) }}<template v-if="data.absorbedTotal > 0"> · absorbido {{ formatCurrency(data.absorbedTotal) }}</template><template v-if="data.voidedTotal > 0"> · anulado {{ formatCurrency(data.voidedTotal) }}</template>
      </span>
      <span class="svc-dist__footer-total">{{ formatCurrency(data.totalDistributed) }}</span>
    </div>

    <ServiceExemptionModal
      v-if="exemptRow && data.periodId"
      :period-id="data.periodId"
      :row="exemptRow"
      @close="exemptRow = null"
      @saved="onExemptionSaved"
    />
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue'
import { getDistribution, deleteServiceExemption } from '@/services/services'
import ServiceExemptionModal from '@/components/Servicios/ServiceExemptionModal.vue'

const props = defineProps({
  parkId: { type: String, required: true },
  period: { type: String, required: true },
})

const emit = defineEmits(['hover-services'])

const viewMode = ref('tenant')
const expandedIds = ref(new Set())
const expandedServiceIds = ref(new Set())
const highlightedRows = ref(new Set())

const emptyData = () => ({ vacantInfo: { count: 0, areaM2: 0 }, rows: [], totalDistributed: 0, totalServicesAmount: 0, matches: true, differenceAmount: 0, exemptTotal: 0, absorbedTotal: 0, voidedTotal: 0, periodId: null, byService: [] })
const data = ref(emptyData())

async function load() {
  const previousRowsMap = new Map(data.value.rows.map((r) => [r.contractId, r.total]))
  const result = await getDistribution(props.parkId, props.period)

  const changedIds = result.rows.filter((r) => previousRowsMap.has(r.contractId) && previousRowsMap.get(r.contractId) !== r.total).map((r) => r.contractId)

  data.value = result

  if (changedIds.length) {
    highlightedRows.value = new Set(changedIds)
    setTimeout(() => { highlightedRows.value = new Set() }, 500)
  }
}

onMounted(load)
watch(() => props.period, load)

function toggleExpand(id) {
  const next = new Set(expandedIds.value)
  if (next.has(id)) next.delete(id)
  else next.add(id)
  expandedIds.value = next
}

function toggleServiceExpand(id) {
  const next = new Set(expandedServiceIds.value)
  if (next.has(id)) next.delete(id)
  else next.add(id)
  expandedServiceIds.value = next
}

const exemptRow = ref(null)
const busyId = ref('')
const exError = ref('')
const exErrorId = ref('')

function canExempt(row) {
  return !!data.value.periodId && !row.exempt && !row.inGrace && row.breakdown.some((b) => b.grossAmount > 0)
}

function exemptionIdFor(row, serviceId) {
  return row.exemptions?.find((e) => e.serviceId === serviceId)?.id || ''
}

async function onExemptionSaved() {
  exemptRow.value = null
  await load()
}

async function removeExemption(row, serviceId) {
  const id = exemptionIdFor(row, serviceId)
  if (!id) return
  busyId.value = id
  exError.value = ''
  try {
    await deleteServiceExemption(data.value.periodId, id)
    await load()
  } catch (err) {
    exError.value = err.message
    exErrorId.value = row.contractId
  } finally {
    busyId.value = ''
  }
}

function onRowHover(row) {
  emit('hover-services', row.breakdown.map((b) => b.serviceId))
}

function formatCurrency(n) {
  if (n === null || n === undefined) return '—'
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(n)
}

defineExpose({ reload: load })
</script>

<style scoped>
.svc-dist {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  background: var(--bg-surface-1);
  border: 1px solid var(--border-active);
  border-radius: 10px;
  overflow: hidden;
  margin: 0 1.5rem 1.5rem 1rem;
  min-height: 0;
}

.svc-dist__header {
  min-height: 48px;
  height: auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0.9rem;
  border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;
}

.svc-dist__title { font-family: var(--font-body); font-size: 11px; font-weight: 500; text-transform: uppercase; letter-spacing: 0.8px; color: #94a3b8; }
.svc-dist__realtime { display: block; font-family: var(--font-body); font-size: 10px; font-style: italic; color: #3b82f6; }

.svc-dist__view-toggle { display: flex; gap: 0.3rem; }
.svc-dist__view-pill {
  background: #f8f9fc; border: 1px solid #e2e8f0; border-radius: 20px; padding: 0.2rem 0.55rem;
  font-family: var(--font-body); font-size: 11px; color: #64748b; cursor: pointer;
}
.svc-dist__view-pill--active { background: #3b82f6; border-color: #3b82f6; color: #ffffff; }

.svc-dist__vacant-note {
  padding: 0.35rem 0.9rem; font-family: var(--font-body); font-size: 10px; color: #94a3b8; flex-shrink: 0; border-bottom: 1px solid #f1f5f9;
}

.svc-dist__body { flex: 1; min-height: 0; overflow-y: auto; }
.svc-dist__body::-webkit-scrollbar { width: 6px; }
.svc-dist__body::-webkit-scrollbar-thumb { background: var(--border-active); border-radius: 3px; }
.svc-dist__body::-webkit-scrollbar-track { background: transparent; }

.svc-dist__row { border-bottom: 1px solid #f1f5f9; cursor: pointer; flex-shrink: 0; transition: background 80ms ease; }
.svc-dist__row:hover { background: #eff6ff; }
.svc-dist__row--exempt { opacity: 0.5; }
.svc-dist__row--highlight { animation: svc-dist-flash 500ms ease; }

@keyframes svc-dist-flash {
  0% { background: #eff6ff; }
  100% { background: transparent; }
}

.svc-dist__row-main { height: 52px; display: flex; align-items: center; gap: 0.6rem; padding: 0 0.9rem; }

.svc-dist__status-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.svc-dist__status-dot--green { background: #10b981; }
.svc-dist__status-dot--amber { background: #f0a500; }
.svc-dist__status-dot--gray { background: #94a3b8; }

.svc-dist__tenant-info { flex: 0 0 30%; min-width: 0; }
.svc-dist__tenant-name { font-family: var(--font-body); font-size: 13px; font-weight: 500; color: #1e3a5f; display: flex; align-items: center; gap: 0.3rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.svc-dist__exempt-tag { font-size: 9px; background: #f1f5f9; color: #64748b; border-radius: 4px; padding: 0.02rem 0.3rem; }
.svc-dist__plot-tag { font-size: 10px; color: #94a3b8; background: #f1f5f9; border-radius: 4px; padding: 0.02rem 0.3rem; }

.svc-dist__area-info { flex: 0 0 20%; font-family: var(--font-body); font-size: 11px; color: #64748b; }
.svc-dist__area-percent { font-size: 10px; color: #94a3b8; }

.svc-dist__amount-wrap { flex: 1; text-align: right; min-width: 0; }
.svc-dist__amount { font-family: var(--font-mono); font-size: 13px; color: #1e3a5f; }
.svc-dist__amount--strike { text-decoration: line-through; color: #94a3b8; }
.svc-dist__amount--gray { color: #94a3b8; }
.svc-dist__grace-note { font-family: var(--font-body); font-size: 9px; color: #d97706; }

.svc-dist__exempt-note { font-family: var(--font-body); font-size: 9px; color: #7c3aed; }
.svc-dist__ex-tag { margin-left: 0.3rem; font-size: 9px; border-radius: 4px; padding: 0.02rem 0.3rem; background: #ede9fe; color: #6d28d9; }
.svc-dist__ex-tag--VOID { background: #f1f5f9; color: #64748b; }
.svc-dist__breakdown-gross { font-family: var(--font-mono); font-size: 11px; color: #94a3b8; text-decoration: line-through; }
.svc-dist__ex-remove { background: none; border: none; color: #94a3b8; cursor: pointer; display: flex; padding: 0; font-size: 14px; }
.svc-dist__ex-remove:hover { color: #dc2626; }
.svc-dist__ex-remove:disabled { opacity: 0.4; cursor: default; }
.svc-dist__ex-actions { display: flex; justify-content: flex-end; margin-top: 0.4rem; }
.svc-dist__ex-btn { display: inline-flex; align-items: center; gap: 0.3rem; background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 0.25rem 0.6rem; font-family: var(--font-body); font-size: 11px; color: #3b82f6; cursor: pointer; }
.svc-dist__ex-btn:hover { background: #eff6ff; border-color: #3b82f6; }
.svc-dist__ex-error { margin: 0.4rem 0 0; font-family: var(--font-body); font-size: 11px; color: #dc2626; }
.svc-dist__footer-exempt { font-family: var(--font-body); font-size: 11px; color: #7c3aed; }

.svc-dist__chevron { font-size: 12px; color: #94a3b8; transition: transform 150ms ease; flex-shrink: 0; }
.svc-dist__chevron--open { transform: rotate(180deg); }

.svc-dist__expanded { background: #fafafa; border-top: 1px solid #f1f5f9; padding: 0.4rem 0.9rem; }
.svc-dist__breakdown-row { height: 32px; display: flex; align-items: center; gap: 0.5rem; font-family: var(--font-body); font-size: 12px; color: #64748b; }
.svc-dist__breakdown-icon { font-size: 14px; color: #94a3b8; flex-shrink: 0; }
.svc-dist__breakdown-name { flex: 1; min-width: 0; }
.svc-dist__breakdown-amount { font-family: var(--font-mono); font-size: 12px; color: #1e3a5f; }

.svc-dist__breakdown-total {
  display: flex; justify-content: space-between; align-items: center; border-top: 1px solid #e2e8f0;
  margin-top: 0.3rem; padding-top: 0.4rem; font-family: var(--font-body); font-size: 12px; font-weight: 500; color: #1e3a5f;
}

.svc-dist__check-icon { color: #16a34a; }
.svc-dist__diff-text { color: #dc2626; font-size: 11px; }

.svc-dist__service-row { border-bottom: 1px solid #f1f5f9; flex-shrink: 0; }
.svc-dist__service-row-main { height: 48px; display: flex; align-items: center; gap: 0.5rem; padding: 0 0.9rem; cursor: pointer; }
.svc-dist__service-row-main:hover { background: #eff6ff; }
.svc-dist__service-name { flex: 1; font-family: var(--font-body); font-size: 13px; color: #1e3a5f; }
.svc-dist__service-amount { font-family: var(--font-mono); font-size: 13px; color: #1e3a5f; }

.svc-dist__footer {
  height: 44px; display: flex; align-items: center; gap: 0.75rem; padding: 0 0.9rem;
  background: #f8f9fc; border-top: 1px solid #e2e8f0; flex-shrink: 0;
}
.svc-dist__footer-label { font-family: var(--font-body); font-size: 12px; font-weight: 500; color: #1e3a5f; }
.svc-dist__footer-check { display: flex; align-items: center; gap: 0.2rem; font-family: var(--font-body); font-size: 11px; color: #16a34a; }
.svc-dist__footer-diff { font-family: var(--font-body); font-size: 11px; color: #dc2626; }
.svc-dist__footer-total { margin-left: auto; font-family: var(--font-mono); font-size: 14px; font-weight: 700; color: #1e3a5f; }

.svc-dist-fade-enter-active, .svc-dist-fade-leave-active { transition: opacity 200ms ease; }
.svc-dist-fade-enter-from, .svc-dist-fade-leave-to { opacity: 0; }
</style>